import { Controller, useFormContext } from "react-hook-form";

import FormArrayField from "ds/components/Form/ArrayField";
import FormField from "ds/components/Form/Field";
import FormFieldRowWrapper from "ds/components/Form/Field/RowWrapper";
import Input from "ds/components/Input";
import Box from "ds/components/Box";

import { validateProviderClaimName, validateSpaceliftClaimName } from "./validators";

export type ClaimMappingFormValue = {
  name: string;
  value: string;
};

type FormFieldValues = Record<"claimMapping", ClaimMappingFormValue[]>;

export const EMPTY_CLAIM_MAPPING_VALUE: ClaimMappingFormValue = { name: "", value: "" };

const FormFieldClaimMapping = () => {
  const { control, getValues, clearErrors } = useFormContext<FormFieldValues>();

  const handleOnKeyDown = (callback?: () => void) => (e: React.KeyboardEvent) => {
    if (callback && e.key === "Enter") {
      callback();
    }
  };

  const handleRemove = () => {
    const fields = getValues("claimMapping");
    if (fields.length === 1 && !fields[0]?.name && !fields[0]?.value) {
      clearErrors("claimMapping");
    }
  };

  return (
    <FormArrayField<FormFieldValues, "claimMapping">
      name="claimMapping"
      addButtonLabel="Add another claim mapping"
      emptyValue={EMPTY_CLAIM_MAPPING_VALUE}
      onRemove={handleRemove}
    >
      {({ value, index, addEmptyItem, suffix }) => (
        <FormField
          key={`claim-${index}`}
          label={index === 0 ? "Claim Mappings" : undefined}
          isOptional
          fullWidth
          noMargin
        >
          <FormFieldRowWrapper>
            <Controller
              name={`claimMapping.${index}.name`}
              control={control}
              rules={{
                validate: (val) =>
                  validateProviderClaimName({
                    value: val,
                    index,
                    claimFields: getValues("claimMapping"),
                  }),
              }}
              render={({ field, fieldState }) => (
                <FormField error={fieldState.error?.message} noMargin fullWidth>
                  {({ ariaInputProps: nameInputProps }) => (
                    <Input
                      placeholder="Provider claim name"
                      value={value.name}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      ref={field.ref}
                      error={!!fieldState.error?.message}
                      {...nameInputProps}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name={`claimMapping.${index}.value`}
              control={control}
              rules={{
                validate: (val) =>
                  validateSpaceliftClaimName({
                    value: val,
                    index,
                    claimFields: getValues("claimMapping"),
                  }),
              }}
              render={({ field, fieldState }) => (
                <FormField error={fieldState.error?.message} noMargin fullWidth>
                  {({ ariaInputProps: valueInputProps }) => (
                    <Box gap="medium">
                      <Input
                        placeholder="Spacelift claim name"
                        value={value.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        ref={field.ref}
                        onKeyDown={handleOnKeyDown(addEmptyItem)}
                        error={!!fieldState.error?.message}
                        {...valueInputProps}
                      />
                      {suffix}
                    </Box>
                  )}
                </FormField>
              )}
            />
          </FormFieldRowWrapper>
        </FormField>
      )}
    </FormArrayField>
  );
};

export default FormFieldClaimMapping;
